import { MaterialCommunityIcons } from "@expo/vector-icons";
import { HStack, Pressable, Toast, VStack } from "native-base";
import * as React from "react";
import {
  StyleSheet,
  ActivityIndicator,
} from "react-native";
import { View, Divider, Text, Icon, Image, Button } from "../components/Styled";
import { useTranslation } from "../contexts/translations";
import { useNavigation } from "@react-navigation/native";
import { StackNavigationProp } from "@react-navigation/stack";
import { MainStackParams } from "../navigation/types";
import styled from "styled-components/native";
import { useTheme } from "../contexts/theme/hooks";
import { DARK_MODE } from "../constants/Colors";
import { useStartupUser, useStartupConfig, useStartupUserActions } from "../contexts/startup/hooks";
import { ScrollView } from "react-native-gesture-handler";
import * as API from "../services/api";
import {
  ACTIVATED,
  BLOCKED,
} from "../constants/card-status";
import { ICard } from "../interfaces/card.interface";
import { CurrentCardholderContext } from "../contexts/current-cardholder";
import { useFeatureFlags } from "../contexts/feature-flags";
import { useAuth } from "../contexts/auth/hooks";
import OrigoSDKModuleInterface from "../OrigoSDKModuleInterface";

const ThemedIcon = styled(Icon)`
  color: ${(props) => props.theme.textColor};
`;

interface userCard {
  userCard: string;
  name: string;
}

export default function CredentialScreen() {
  const { t } = useTranslation();
  const { allowUserManageCredentials } = useStartupConfig();
  const navigation = useNavigation<StackNavigationProp<MainStackParams>>();
  const [cardsPerType, setCardsPerType] = React.useState<ICard[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [theme] = useTheme();
  const {cardholder} = React.useContext(CurrentCardholderContext);
  const { MANAGE_CARD } = useFeatureFlags();
  const [canRequestCard, setCanRequestCard] = React.useState(MANAGE_CARD);
  const [requestCardLoading, setRequestCardLoading] = React.useState(false);
  const { refreshAuth } = useAuth();
  const { reload, updateDigitalCard } = useStartupUserActions();
  const data = useStartupUser();
  const [canAddPass, setCanAddPass] = React.useState<boolean>(false);

  async function createCard() {
    setRequestCardLoading(true);
    try {
      await refreshAuth();
      await API.createCard({ cardholder: data?.cardholderId || "" });
      await reload();
      setRequestCardLoading(false);
      setCanRequestCard(false);
      Toast.show({
        accessible: true,
        accessibilityLabel: t("digital_id.request_card.success"),
        duration: 8000,
        render: () => (
          <View bgColor="green" style={{ padding: 10 }}>
            <Text color="white">{t("digital_id.request_card.success")}</Text>
          </View>
        ),
      });
      navigation.goBack();
    } catch (error: any) {
      setRequestCardLoading(false);
      Toast.show({
        accessible: true,
        accessibilityLabel: t("digital_id.request_card.error"),
        duration: 8000,
        render: () => (
          <View bgColor="orange" style={{ padding: 10 }}>
            <Text color="white">{t("digital_id.request_card.error")}</Text>
          </View>
        ),
      });
      console.warn("Failed requesting a card", error.response.data);
    }
  }

  function loadComponent() {
      return (
        <ActivityIndicator
          accessible
          accessibilityLabel={t("accessibility.loading", {
            text: "",
          })}
          color="white"
        />
      );
    }

  function gotoCredentialAction(val: any) {
    const params: userCard = {
      userCard: val,
      name: val.type.name,
    };
    navigation.navigate("CredentialAction", params);
  }

  React.useEffect(() => {
    (async () => {
      setLoading(true);
      setRequestCardLoading(true);
      try {
        const resultData = await API.getCardCredentials(cardholder.id);
        if (resultData.data && resultData.data[0]) {
          const cardstype = Array.from(resultData.data).find(
            (s: any) => s.cardholder.id == cardholder.id
          )?.cardsPerType;

          var newCards: ICard[] = [];
          cardstype.map((value: any) => {
            if(value.cards?.length){
              let activeCards = value.cards.filter((card: ICard) => card.status == 'activated');
              if(activeCards?.length){
                newCards.push(...activeCards);
              }else{
                let notWithdrawnCards = value.cards.filter((card: ICard) => card.status != 'withdrawn');

                if (!notWithdrawnCards.length) {
                  return;
                }

                let latestCard = [...notWithdrawnCards].reduce((newest, current) => {
                  return new Date(current.modified) > new Date(newest.modified) ? current : newest;
                }, notWithdrawnCards[0]);
                newCards.push(latestCard);
              }
            }
          });

          setCardsPerType(newCards);
          OrigoSDKModuleInterface.canAddAppleWalletPass().then( res => setCanAddPass(res.message))
        }
      } catch (error) {
        console.log("error", error);
      } finally {
        setLoading(false);
        setRequestCardLoading(false);
      }
    })();
  }, []);

  return (
    <View
      $transparent
      height={300}
      as={ScrollView}
      contentContainerStyle={{ flex: 1 }}
    >
      <ScrollView>
        <Divider $fullSize spaceVertical={1} />
        {
          cardsPerType.length > 0 ? (
            <>
            {
              cardsPerType.map((val, index) => (
                <View key={index} $transparent>
                  <Pressable
                    disabled={!allowUserManageCredentials}
                    onPress={() => gotoCredentialAction(val)}
                  >
                    <HStack
                      style={styles.pressableMennu}
                      justifyContent="space-between"
                    >
                      <VStack space={0}>
                        <Text size={15} style={{ paddingTop: 5 }}>
                          {val?.type?.name}
                        </Text>
                        <View $transparent flexDirection="row" spaceVertical={5}>
                          <Text
                            size={13}
                            style={{ paddingVertical: 5, paddingRight: 15 }}
                            $bold
                            color={
                              val?.status === BLOCKED
                                ? "red"
                                : val?.status === ACTIVATED
                                ? "green"
                                : "orange"
                            }
                          >
                            {
                              t(`credential_status.${val?.status}`).toUpperCase()
                            }
                          </Text>
                          {(val?.type?.category?.digitalCredentialSystem ===
                            "hid_mobile_access" || val?.type?.category?.digitalCredentialSystem ===
                            "hid_apple_wallet") && (
                            <View $transparent width={50} height={25}>
                              <Image
                                style={{
                                  flex: 1,
                                  ...StyleSheet.absoluteFillObject,
                                  resizeMode: "contain",
                                  width: 50,
                                  height: 25,
                                }}
                                source={
                                  theme === DARK_MODE
                                    ? require("../assets/images/seos-logo/SEOS-by-ASSA-ABLOY-RGB-White.png")
                                    : require("../assets/images/seos-logo/SEOS-by-ASSA-ABLOY-RGB-Black.png")
                                }
                              />
                            </View>
                          )}
                        </View>
                      </VStack>
                      <HStack space={5} style={{ alignItems: "center" }}>
                        {val?.type?.category?.digitalCredentialSystem ===
                          "salto_space" &&
                          val?.type?.category?.isDigital &&
                          val?.status === ACTIVATED && (
                            <View
                              style={{ flexDirection: "row", alignItems: "center" }}
                              $transparent
                              width={40}
                              height={50}
                            >
                              
                            </View>
                          )}
                        {allowUserManageCredentials && (
                          <ThemedIcon
                            size={40}
                            as={MaterialCommunityIcons}
                            name="chevron-right"
                          />
                        )}
                      </HStack>
                    </HStack>
                  </Pressable>
  
                  <Divider $fullSize spaceVertical={1} />
                </View>
            ))
            }
            {canAddPass ? 
            <View space={20}>
              <Button onPress={createCard} isDisabled={requestCardLoading}>
                    {
                      requestCardLoading ? loadComponent() : "Request a new Pass"
                    }
              </Button>
            </View> : null
            }
            </>
        ) 
        : <View $transparent justifyContent="center" space={20}>
            <Text $textAlign="center">
              {
                t("digital_id.card.do_not_have_card")
              }
            </Text>
            <View space={20} />
            {
              (canRequestCard && allowUserManageCredentials) ?
              <Button onPress={createCard} isDisabled={requestCardLoading}>
                {
                  requestCardLoading ? loadComponent() : t("digital_id.card.request_card")
                }
              </Button> : null
            }
            
          </View>
        }
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  pressableMennu: {
    paddingVertical: 10,
  },
  loadContainer: {
    flex: 1,
    justifyContent: "center",
  },
});
